import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import CrewRouteMap from "./CrewRouteMap";
import GenerateScheduleButton from "./GenerateScheduleButton";
import ProofPhotoButton from "./ProofPhotoButton";
import {
  CalendarDays,
  Clock,
  Receipt,
  Truck,
  Users,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";
import { companyOrSharedDemo } from "@/lib/demo";

const CATCH_UP_NOTE = "Catch-up: missed service";

type Job = {
  id: string;
  customer_name: string;
  address: string;
  status: string;
  crew: string | null;
  sequence: number | null;
  est_duration_minutes: number | null;
  actual_duration_minutes: number | null;
  completed_at: string | null;
  notes: string | null;
  completion_photo_path: string | null;
  lat: number | null;
  lng: number | null;
};

type CrewEndpoint = {
  name: string;
  start_address: string | null;
  end_address: string | null;
  start_lat: number | null;
  start_lng: number | null;
  end_lat: number | null;
  end_lng: number | null;
};

type Invoice = { id: string; amount: number; status: string; due_on: string | null; issued_on: string };

type Props = {
  companyId: string;
  companyName?: string;
  onGoTo: (section: "schedule" | "billing" | "crew" | "customers" | "setup") => void;
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

const money = (n: number) =>
  n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const fmtHours = (min: number) => {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h ? `${h}h ${m ? `${m}m` : ""}`.trim() : `${m}m`;
};

const DONE = ["complete", "completed", "complete - paid", "complete - not paid", "paid"];
const isDone = (s: string) => DONE.includes(s.toLowerCase());
const isSkipped = (s: string) => s.toLowerCase().includes("skip");

export default function Today({ companyId, companyName, onGoTo }: Props) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  const [missedCount, setMissedCount] = useState(0);
  const [endpoints, setEndpoints] = useState<Record<string, CrewEndpoint>>({});
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const date = todayISO();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [jobRes, invRes, missedRes, epRes] = await Promise.all([
        supabase
          .from("jobs")
          .select(
            "id, customer_name, address, status, crew, sequence, est_duration_minutes, actual_duration_minutes, completed_at, notes, completion_photo_path, lat, lng"
          )
          .eq("company_id", companyId)
          .eq("scheduled_date", date)
          .order("sequence", { ascending: true }),
        supabase
          .from("invoices")
          .select("id, amount, status, due_on, issued_on")
          .eq("company_id", companyId)
          .neq("status", "void"),
        supabase
          .from("customers")
          .select("id", { count: "exact", head: true })
          .or(companyOrSharedDemo(companyId))
          .eq("source", "Legacy")
          .eq("missed_status", "needs_rescheduling"),
        supabase
          .from("crews")
          .select("name, start_address, end_address, start_lat, start_lng, end_lat, end_lng")
          .eq("company_id", companyId),
      ]);
      if (cancelled) return;
      setJobs((jobRes.data as Job[]) ?? []);
      setInvoices((invRes.data as Invoice[]) ?? []);

      setMissedCount(missedRes.count ?? 0);
      const epMap: Record<string, CrewEndpoint> = {};
      ((epRes.data as CrewEndpoint[]) ?? []).forEach((c) => (epMap[c.name] = c));
      setEndpoints(epMap);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, date, reloadKey]);

  const load = () => setReloadKey((k) => k + 1);


  const stats = useMemo(() => {
    const done = jobs.filter((j) => isDone(j.status));
    const skipped = jobs.filter((j) => isSkipped(j.status));
    const remaining = jobs.length - done.length - skipped.length;
    const crews = Array.from(new Set(jobs.map((j) => j.crew).filter(Boolean))) as string[];
    const remainingMinutes = jobs
      .filter((j) => !isDone(j.status) && !isSkipped(j.status))
      .reduce((s, j) => s + (j.est_duration_minutes ?? 45), 0);

    const remainingByCrew = new Map<string, { minutes: number; stops: number }>();
    jobs
      .filter((j) => !isDone(j.status) && !isSkipped(j.status))
      .forEach((j) => {
        const key = j.crew ?? "Unassigned";
        const cur = remainingByCrew.get(key) ?? { minutes: 0, stops: 0 };
        cur.minutes += j.est_duration_minutes ?? 45;
        cur.stops += 1;
        remainingByCrew.set(key, cur);
      });

    const unpaid = invoices.filter((i) => i.status !== "paid");
    const overdue = unpaid.filter((i) => {
      const ref = i.due_on ?? i.issued_on;
      return new Date(`${ref}T00:00:00`) < new Date(`${date}T00:00:00`);
    });

    return {
      total: jobs.length,
      done: done.length,
      skipped: skipped.length,
      remaining,
      crews,
      remainingMinutes,
      remainingByCrew,
      unpaidTotal: unpaid.reduce((s, i) => s + Number(i.amount), 0),
      overdueCount: overdue.length,
      overdueTotal: overdue.reduce((s, i) => s + Number(i.amount), 0),
    };
  }, [jobs, invoices, date]);

  const byCrew = useMemo(() => {
    const map = new Map<string, Job[]>();
    jobs.forEach((j) => {
      const key = j.crew ?? "Unassigned";
      map.set(key, [...(map.get(key) ?? []), j]);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [jobs]);

  const pct = stats.total ? Math.round((stats.done / stats.total) * 100) : 0;

  const dayLabel = new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  if (loading) {
    return (
      <div className="space-y-3 sm:space-y-4">
        <Skeleton className="h-7 w-56 sm:h-8 sm:w-64" />
        <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[72px] w-full sm:h-24" />
          ))}
        </div>
        <Skeleton className="h-44 w-full sm:h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-2 sm:gap-3">
        <div>
          <h2 className="font-display text-base font-semibold sm:text-lg">Today</h2>
          <p className="text-xs text-muted-foreground sm:text-sm">{dayLabel}</p>
        </div>
        <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs sm:h-9 sm:px-3" onClick={() => onGoTo("schedule")}>
          Open job schedule <ArrowRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
            <CardTitle className="text-xs text-muted-foreground sm:text-sm">Stops today</CardTitle>
            <CalendarDays className="h-3.5 w-3.5 text-muted-foreground sm:h-4 sm:w-4" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6">
            <p className="text-xl font-bold sm:text-2xl">{stats.total}</p>
            <p className="text-[11px] text-muted-foreground sm:text-xs">
              {stats.done} completed · {stats.remaining} remaining
              {stats.skipped > 0 && ` · ${stats.skipped} skipped`}
            </p>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted sm:mt-2">
              <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
            <CardTitle className="text-xs text-muted-foreground sm:text-sm">Work left</CardTitle>
            <Clock className="h-3.5 w-3.5 text-muted-foreground sm:h-4 sm:w-4" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6">
            <p className="text-xl font-bold sm:text-2xl">{fmtHours(stats.remainingMinutes)}</p>
            {stats.remainingByCrew.size > 0 ? (
              <ul className="mt-1 space-y-0.5">
                {Array.from(stats.remainingByCrew.entries()).map(([crew, r]) => (
                  <li
                    key={crew}
                    className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground sm:text-xs"
                  >
                    <span className="truncate">{crew}</span>
                    <span className="shrink-0">
                      {fmtHours(r.minutes)} · {r.stops} stop{r.stops === 1 ? "" : "s"}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[11px] text-muted-foreground sm:text-xs">All done</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
            <CardTitle className="text-xs text-muted-foreground sm:text-sm">Outstanding</CardTitle>
            <Receipt className="h-3.5 w-3.5 text-muted-foreground sm:h-4 sm:w-4" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6">
            <p className="text-xl font-bold sm:text-2xl">{money(stats.unpaidTotal)}</p>
            <p className="text-[11px] text-muted-foreground sm:text-xs">
              {stats.overdueCount > 0
                ? `${stats.overdueCount} overdue · ${money(stats.overdueTotal)}`
                : "Nothing overdue"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Needs attention */}
      {(stats.overdueCount > 0 || missedCount > 0) && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-xs sm:text-sm">
              <AlertTriangle className="h-3.5 w-3.5 text-destructive sm:h-4 sm:w-4" />
              Needs attention
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2 p-3 sm:p-6">
            {stats.overdueCount > 0 && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onGoTo("billing")}>
                {stats.overdueCount} overdue invoice{stats.overdueCount === 1 ? "" : "s"}
              </Button>
            )}
            {missedCount > 0 && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onGoTo("customers")}>
                {missedCount} customer{missedCount === 1 ? "" : "s"} need rescheduling
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Today's route */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
            <Truck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Today&apos;s route
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 p-3 sm:space-y-4 sm:p-6">
          {stats.total === 0 ? (
            <div className="rounded-lg border border-dashed p-4 text-center sm:p-8">
              <p className="text-sm font-medium">No stops scheduled for today</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Recurring customers are scheduled automatically overnight. You can also build the
                day manually.
              </p>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                <GenerateScheduleButton
                  companyId={companyId}
                  date={date}
                  label="Build today's schedule"
                  onDone={load}
                />
                <Button size="sm" variant="outline" onClick={() => onGoTo("schedule")}>
                  Go to job schedule
                </Button>
              </div>

            </div>
          ) : (
            <>
              <CrewRouteMap
                crews={byCrew.map(([name, list]) => {
                  const ep = endpoints[name];
                  return {
                    name,
                    jobs: list.map((j) => ({
                      id: j.id,
                      customer_name: j.customer_name,
                      address: j.address,
                      lat: j.lat,
                      lng: j.lng,
                      catchUp: (j.notes ?? "").startsWith(CATCH_UP_NOTE),
                    })),
                    start:
                      ep?.start_lat != null && ep?.start_lng != null
                        ? { lat: ep.start_lat, lng: ep.start_lng, address: ep.start_address ?? "" }
                        : null,
                    end:
                      ep?.end_lat != null && ep?.end_lng != null
                        ? {
                            lat: ep.end_lat,
                            lng: ep.end_lng,
                            address: ep.end_address || ep.start_address || "",
                          }
                        : null,
                  };
                })}
              />

              <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3 sm:gap-3">
                {byCrew.map(([crew, list]) => {
                  const done = list.filter((j) => isDone(j.status)).length;
                  return (
                    <div key={crew} className="rounded-lg border p-2 sm:p-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="flex items-center gap-1.5 text-sm font-semibold">
                          <Users className="h-3.5 w-3.5 text-muted-foreground" />
                          {crew}
                        </p>
                        <Badge variant={done === list.length ? "secondary" : "outline"} className="text-[10px]">
                          {done}/{list.length}
                        </Badge>
                      </div>
                      <ul className="mt-1.5 space-y-0.5">
                        {list.map((j) => (
                          <li
                            key={j.id}
                            className="flex items-center justify-between gap-2 text-xs"
                          >
                            <span
                              className={
                                isDone(j.status)
                                  ? "truncate text-muted-foreground line-through"
                                  : "truncate"
                              }
                            >
                              {j.customer_name}
                            </span>
                            {(j.notes ?? "").startsWith(CATCH_UP_NOTE) && (
                              <Badge className="shrink-0 border-amber-500 bg-amber-500/15 text-[10px] text-amber-700 hover:bg-amber-500/15">
                                Catch-up
                              </Badge>
                            )}
                            {j.completion_photo_path && (
                              <ProofPhotoButton
                                compact
                                path={j.completion_photo_path}
                                customerName={j.customer_name}
                                completedAt={j.completed_at}
                              />
                            )}
                            <span className="shrink-0 text-[10px] uppercase text-muted-foreground">
                              {j.status}
                            </span>

                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
